/* eslint-disable @typescript-eslint/no-unused-vars */
import { useFormContext } from "components/Form";
import { Button } from "components/Button";
import { useStepperContext } from "components/Steps";
import { AutoComplete } from "components/hookForm/AutoComplete";
import { useMemo, useState, useEffect } from "react";

import { ArrowForward as IconArrowNext } from "@material-ui/icons";
import { ArrowBack as IconArrowBack } from "@material-ui/icons";
import { Radio } from "components/hookForm/Radio";

import * as S from "./styles";
import { DatePicker } from "components/hookForm/DatePicker";
import { DefaultFooter } from "../../../components/DefaultFooter";
import { InputCurrency } from "components/hookForm/InputCurrency";
import * as dateFns from "date-fns";
import { MediaMatch } from "components/MediaMatch";
import { Tooltip } from "@material-ui/core";

export function FinancingDataForm() {
  const {
    methodsForm: {
      formState: { errors },
      watch,
      setValue,
      trigger,
    },
    validateFields,
  } = useFormContext();

  const dateBirthdayWatch = watch("dateBirthday");

  const [showDeadline, setShowDeadline] = useState(false);
  const [deadLineMonths, setDeadLineMonths] = useState(0);

  useEffect(() => {
    if (!dateBirthdayWatch || !!errors.dateBirthday) {
      setShowDeadline(false);
      setValue("deadline", null);
      return;
    }

    setShowDeadline(true);

    const age = dateFns.differenceInYears(
      new Date(),
      new Date(dateBirthdayWatch)
    );

    let months = (80 - age) * 12;

    if (months > 420) {
      months = 420;
    }

    setDeadLineMonths(months);
  }, [dateBirthdayWatch, errors.dateBirthday, setValue]);

  const { goToNextStep, goToPreviousStep } = useStepperContext();

  async function handleNextStep() {
    const fields = [
      "city",
      "approximateAmount",
      "financingAmount",
      "dateBirthday",
      "amortizationType",
      "deadline",
    ];

    const isFieldsValids = await validateFields(fields);

    if (isFieldsValids) {
      goToNextStep();
    }
  }

  const statesOptions = useMemo(() => {
    return [
      { value: "AC", label: "Acre" },
      { value: "AL", label: "Alagoas" },
      { value: "AP", label: "Amapá" },
      { value: "AM", label: "Amazonas" },
      { value: "BA", label: "Bahia" },
      { value: "CE", label: "Ceará" },
      { value: "DF", label: "Distrito Federal" },
      { value: "ES", label: "Espírito Santo" },
      { value: "GO", label: "Goías" },
      { value: "MA", label: "Maranhão" },
      { value: "MT", label: "Mato Grosso" },
      { value: "MS", label: "Mato Grosso do Sul" },
      { value: "MG", label: "Minas Gerais" },
      { value: "PA", label: "Pará" },
      { value: "PB", label: "Paraíba" },
      { value: "PR", label: "Paraná" },
      { value: "PE", label: "Pernambuco" },
      { value: "PI", label: "Piauí" },
      { value: "RJ", label: "Rio de Janeiro" },
      { value: "RN", label: "Rio Grande do Norte" },
      { value: "RS", label: "Rio Grande do Sul" },
      { value: "RO", label: "Rondônia" },
      { value: "RR", label: "Roraíma" },
      { value: "SC", label: "Santa Catarina" },
      { value: "SP", label: "São Paulo" },
      { value: "SE", label: "Sergipe" },
      { value: "TO", label: "Tocantins" },
    ];
  }, []);

  const deadLinesOptions = useMemo(() => {
    const monthsList = Array.from(
      { length: deadLineMonths },
      (_, i) => i + 1
    ).filter((item) => item >= 36);

    return monthsList
      .sort((numberA, numberB) => numberB - numberA)
      .map((month) => {
        let stringMonth = "meses";

        if (month === 1) {
          stringMonth = "mês";
        }

        return {
          value: `${month} ${stringMonth}`,
          label: `${month} ${stringMonth}`,
        };
      });
  }, [deadLineMonths]);

  return (
    <S.Container>
      <S.Title>
        Os dados que pediremos a seguir são importantes para simularmos o seu
        financiamento.
      </S.Title>

      <S.WrapperForm>
        <div className="grid-3">
          <AutoComplete
            name="city"
            customLabel="Localização do imóvel*"
            options={statesOptions}
            propertyLabel="label"
            error={!!errors.city}
            helperText={errors.city?.message}
            defaultValue="ES"
          />

          <InputCurrency
            max={9999999999}
            name="approximateAmount"
            placeholder="R$"
            customLabel="Valor aproximado do imóvel*"
            error={!!errors.approximateAmount}
            helperText={errors.approximateAmount?.message}
            onChangeCapture={() =>
              !!errors.financingAmount && trigger("financingAmount")
            }
          />

          <InputCurrency
            max={9999999999}
            name="financingAmount"
            placeholder="R$"
            customLabel="Valor do financiamento*"
            error={!!errors.financingAmount}
            helperText={errors.financingAmount?.message}
          />
        </div>

        <div className="grid-3">
          <Radio
            name="amortizationType"
            customLabel="Selecione o tipo de amortização*"
            row
            options={[
              {
                label: "Sistema SAC",
                value: "Sistema SAC",
                tooltipText: "Parcelas decrescentes, com amortização constante",
              },
              {
                label: "Sistema PRICE",
                value: "Sistema PRICE",
                tooltipText:
                  "Parcelas fixas, com diminuição gradual dos juros e aumento proporcional da amortização",
              },
            ]}
            error={!!errors.amortizationType}
            helperText={errors.amortizationType?.message}
          />

          <Tooltip
            title="Se casal, o de maior idade"
          >
            <div>
              <DatePicker
                name="dateBirthday"
                customLabel="Data de nascimento *"
                placeholder="dd/mm/aaaa"
                helperText={errors.dateBirthday?.message}
                error={!!errors.dateBirthday}
                fullWidth={true}
              />
            </div>
          </Tooltip>

          {showDeadline && (
            <AutoComplete
              name="deadline"
              customLabel="Prazo desejado para pagar*"
              options={deadLinesOptions}
              propertyLabel="label"
              error={!!errors.deadline}
              helperText={errors.deadline?.message}
            />
          )}
        </div>
      </S.WrapperForm>

      <S.WrapperButtons>
        <Button
          onClick={goToPreviousStep}
          startIcon={<IconArrowBack />}
          variant="outlined"
        >
          VOLTAR
        </Button>
        <Button onClick={handleNextStep} endIcon={<IconArrowNext />}>
          AVANÇAR
        </Button>
      </S.WrapperButtons>

      <MediaMatch greaterThan="medium">
        <DefaultFooter />
      </MediaMatch>
    </S.Container>
  );
}
